import React from 'react';
import { Card, Button, Row, Col, Typography, Timeline, Table } from 'antd';
import { useHistory } from 'react-router-dom';
import { ArrowLeftOutlined, BuildOutlined, CheckCircleOutlined, SettingOutlined, SafetyOutlined } from '@ant-design/icons';
import calmiHeartImage from '../../assets/images/Calmi Heart.png';

const { Title, Text, Paragraph } = Typography;

const CalmiHeartOEMs = () => {
  const history = useHistory();

  const hardwareSpecs = [
    { component: 'Microcontroller', specification: 'Raspberry Pi 4 or equivalent ARM processor', notes: 'Minimum 2GB RAM recommended' },
    { component: 'Microphone Array', specification: '2-4 element MEMS microphone array', notes: 'Optimized for child voice pickup' },
    { component: 'Speaker', specification: '3W-5W full-range driver', notes: 'High clarity for TTS output' },
    { component: 'WiFi Module', specification: '802.11n/ac dual-band', notes: 'Cloud connectivity for AI processing' },
    { component: 'LED Indicators', specification: 'RGB LED array (3-5 LEDs)', notes: 'Status indication (listening/thinking/speaking)' },
    { component: 'Battery', specification: '3000-5000mAh Li-ion rechargeable', notes: '6-12 hours operation time' },
    { component: 'Push Button', specification: 'Tactile push-to-talk button', notes: 'Optional - improves noise control' },
    { component: 'Charging Port', specification: 'USB-C or wireless charging pad', notes: 'Child-safe implementation required' }
  ];

  const manufacturingSteps = [
    {
      title: 'Hardware Integration Planning',
      description: 'We provide detailed hardware layouts and integration guides for embedding components into your plush designs.',
      color: '#1890ff'
    },
    {
      title: 'Prototype Development',
      description: 'Joint prototyping phase with our engineering team to ensure optimal component placement and functionality.',
      color: '#52c41a'
    },
    {
      title: 'Safety Certification Support',
      description: 'Assistance with FCC, CE, and child toy safety certifications. COPPA compliance guidance included.',
      color: '#faad14'
    },
    {
      title: 'Production Line Setup',
      description: 'Manufacturing process documentation and quality control checkpoints for consistent production.',
      color: '#722ed1'
    },
    {
      title: 'Testing & Validation',
      description: 'Comprehensive testing protocols including audio quality, AI responsiveness, and safety validation.',
      color: '#eb2f96'
    },
    {
      title: 'Launch Support',
      description: 'Ongoing technical support during product launch and initial production runs.',
      color: '#13c2c2'
    }
  ];

  const columns = [
    {
      title: 'Component',
      dataIndex: 'component',
      key: 'component',
      render: (text) => <Text strong>{text}</Text>
    },
    {
      title: 'Specification',
      dataIndex: 'specification',
      key: 'specification'
    },
    {
      title: 'Implementation Notes',
      dataIndex: 'notes',
      key: 'notes',
      render: (text) => <Text type="secondary">{text}</Text>
    }
  ];

  return (
    <div style={{ minHeight: '100vh', background: '#f0f2f5' }}>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: '60px 0',
        marginBottom: '40px'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
          <Button 
            type="text" 
            icon={<ArrowLeftOutlined />} 
            onClick={() => history.push('/heart')}
            style={{ color: 'white', marginBottom: '20px' }}
          >
            Back to Calmi Heart
          </Button>
          
          <Row align="middle" gutter={[32, 32]}>
            <Col xs={24} md={16}>
              <img 
                src={calmiHeartImage} 
                alt="Calmi Heart"
                style={{ 
                  height: '80px',
                  objectFit: 'contain',
                  marginBottom: '16px'
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.parentNode.innerHTML = '<div style="font-size: 48px; color: white; margin-bottom: 16px;">🏭</div>';
                }}
              />
              <Title level={1} style={{ color: 'white', marginBottom: '16px' }}>
                For OEMs & Manufacturers
              </Title>
              <Paragraph style={{ color: 'rgba(255,255,255,0.9)', fontSize: '20px', marginBottom: '0' }}>
                Complete hardware solutions and manufacturing support for integrating 
                Calmi's AI companion technology into your plush toy production lines.
              </Paragraph>
            </Col>
            <Col xs={24} md={8}>
              <div style={{
                background: 'rgba(255,255,255,0.1)',
                borderRadius: '12px',
                padding: '24px',
                textAlign: 'center',
                backdropFilter: 'blur(10px)'
              }}>
                <div style={{ fontSize: '32px', marginBottom: '8px' }}>🏭</div>
                <Text style={{ color: 'white', fontSize: '16px', fontWeight: 'bold' }}>
                  Production Ready
                </Text>
              </div>
            </Col>
          </Row>
        </div>
      </div>

      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
        
        {/* Hardware Specifications */}
        <Card style={{ marginBottom: '40px', borderRadius: '12px' }}>
          <Title level={2} style={{ marginBottom: '24px' }}>
            <SettingOutlined style={{ marginRight: '12px', color: '#1890ff' }} />
            Hardware Component Specifications
          </Title>
          <Paragraph style={{ fontSize: '16px', color: '#666', marginBottom: '32px' }}>
            Detailed specifications for all hardware components required to build AI-enabled plush companions.
            Our engineering team provides complete integration support and sourcing recommendations.
          </Paragraph>
          
          <Table 
            dataSource={hardwareSpecs}
            columns={columns}
            pagination={false}
            size="large"
            style={{ marginBottom: '24px' }}
          />
          
          <div style={{ 
            background: '#f0f9ff', 
            padding: '20px', 
            borderRadius: '8px',
            border: '1px solid #d3adf7'
          }}>
            <Title level={4} style={{ color: '#722ed1', marginBottom: '12px' }}>
              💡 Engineering Support Included
            </Title>
            <ul style={{ marginBottom: 0 }}>
              <li>Component sourcing recommendations and supplier connections</li>
              <li>PCB design files and assembly instructions</li>
              <li>3D printable housing designs for rapid prototyping</li>
              <li>EMI/EMC compliance guidance and testing support</li>
            </ul>
          </div>
        </Card>

        {/* Manufacturing Process */}
        <Card style={{ marginBottom: '40px', borderRadius: '12px' }}>
          <Title level={2} style={{ marginBottom: '24px' }}>
            <BuildOutlined style={{ marginRight: '12px', color: '#52c41a' }} />
            Manufacturing Process & Support
          </Title>
          <Paragraph style={{ fontSize: '16px', color: '#666', marginBottom: '32px' }}>
            Our comprehensive manufacturing support program guides you through every step 
            from prototype to production, ensuring consistent quality and optimal integration.
          </Paragraph>
          
          <Timeline mode="left">
            {manufacturingSteps.map((step, index) => (
              <Timeline.Item 
                key={index}
                color={step.color}
                dot={<CheckCircleOutlined style={{ fontSize: '16px' }} />}
              >
                <div style={{ paddingLeft: '16px' }}>
                  <Title level={4} style={{ marginBottom: '8px', color: step.color }}>
                    {step.title}
                  </Title>
                  <Paragraph style={{ fontSize: '14px', color: '#666' }}>
                    {step.description}
                  </Paragraph>
                </div>
              </Timeline.Item>
            ))}
          </Timeline>
        </Card>

        {/* Quality & Safety Standards */}
        <Row gutter={[24, 24]} style={{ marginBottom: '40px' }}>
          <Col xs={24} md={12}>
            <Card style={{ height: '100%', borderRadius: '12px' }}>
              <SafetyOutlined style={{ fontSize: '32px', color: '#f5222d', marginBottom: '16px' }} />
              <Title level={3} style={{ marginBottom: '16px' }}>Safety & Compliance</Title>
              <ul style={{ fontSize: '14px' }}>
                <li><strong>Child Safety Standards:</strong> ASTM F963, EN 71, CPSIA compliance</li>
                <li><strong>Electronic Safety:</strong> UL, CE, FCC certification support</li>
                <li><strong>Data Privacy:</strong> COPPA compliance built-in</li>
                <li><strong>Battery Safety:</strong> UN38.3 shipping and safety standards</li>
                <li><strong>Material Safety:</strong> Phthalate-free, lead-free components</li>
              </ul>
            </Card>
          </Col>
          <Col xs={24} md={12}>
            <Card style={{ height: '100%', borderRadius: '12px' }}>
              <CheckCircleOutlined style={{ fontSize: '32px', color: '#52c41a', marginBottom: '16px' }} />
              <Title level={3} style={{ marginBottom: '16px' }}>Quality Assurance</Title>
              <ul style={{ fontSize: '14px' }}>
                <li><strong>Audio Testing:</strong> Voice recognition accuracy validation</li>
                <li><strong>AI Response Quality:</strong> Conversation appropriateness testing</li>
                <li><strong>Durability Testing:</strong> Child-use simulation testing</li>
                <li><strong>Battery Life:</strong> Performance validation across use cases</li>
                <li><strong>WiFi Performance:</strong> Connectivity reliability testing</li>
              </ul>
            </Card>
          </Col>
        </Row>

        {/* Pricing & Volume */}
        <Card style={{ marginBottom: '40px', borderRadius: '12px' }}>
          <Title level={2} style={{ marginBottom: '24px' }}>
            Volume Pricing & Support Tiers
          </Title>
          <Row gutter={[32, 32]}>
            <Col xs={24} md={8}>
              <div style={{ 
                border: '2px solid #d9d9d9', 
                borderRadius: '12px', 
                padding: '24px',
                textAlign: 'center',
                background: '#fafafa'
              }}>
                <Title level={4}>Prototype</Title>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1890ff', marginBottom: '8px' }}>
                  1-100 units
                </div>
                <Text>Full engineering support, prototype development, and testing</Text>
              </div>
            </Col>
            <Col xs={24} md={8}>
              <div style={{ 
                border: '2px solid #1890ff', 
                borderRadius: '12px', 
                padding: '24px',
                textAlign: 'center',
                background: '#f0f9ff'
              }}>
                <Title level={4}>Production</Title>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1890ff', marginBottom: '8px' }}>
                  1,000-10,000 units
                </div>
                <Text>Volume pricing, production line setup, quality assurance</Text>
              </div>
            </Col>
            <Col xs={24} md={8}>
              <div style={{ 
                border: '2px solid #52c41a', 
                borderRadius: '12px', 
                padding: '24px',
                textAlign: 'center',
                background: '#f6ffed'
              }}>
                <Title level={4}>Enterprise</Title>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#52c41a', marginBottom: '8px' }}>
                  10,000+ units
                </div>
                <Text>Custom engineering, dedicated support, supply chain management</Text>
              </div>
            </Col>
          </Row>
        </Card>

        {/* CTA */}
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '40px',
          textAlign: 'center',
          boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
          marginBottom: '60px'
        }}>
          <Title level={3}>Ready to Start Manufacturing?</Title>
          <Paragraph style={{ fontSize: '16px', color: '#666', marginBottom: '24px' }}>
            Contact our manufacturing team to discuss your specific requirements and get started 
            with prototype development.
          </Paragraph>
          <Button
            type="primary"
            size="large"
            style={{
              background: '#1890ff',
              borderColor: '#1890ff',
              height: '48px',
              fontSize: '16px',
              fontWeight: 'bold',
              padding: '0 32px',
              borderRadius: '24px',
              marginRight: '16px'
            }}
          >
            Contact Manufacturing Team
          </Button>
          <Button
            size="large"
            onClick={() => history.push('/heart/technical')}
            style={{
              height: '48px',
              fontSize: '16px',
              padding: '0 32px',
              borderRadius: '24px'
            }}
          >
            View Technical Specs
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CalmiHeartOEMs;